// src/belediye/pages/sistem/SistemVatandasDetayPage.js
//
// FAZ-24 BLOK-C.3 — Vatandaş detay: bilgi + düzenle (ad/soyad/email, telefon HARİÇ)
// + telefon değiştir (AYRI gate citizens.phone.update, KVKK) + başvuru geçmişi +
// pasifleştir (soft-delete, YALNIZ superuser). Gate: citizens.view (route).

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import {
  fetchCitizen,
  fetchCitizenApplications,
  updateCitizen,
  updateCitizenPhone,
  deleteCitizen,
} from "../../services/yonetimApi";
import "./yonetim-ekrani.css";

const backendError = (ex, fallback) => {
  const d = ex?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d) {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return fallback;
};

const fmtTarih = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// REVIEW #9: backend ham status kodu döner → insan-okunur etiket (Application.STATUS_CHOICES)
const DURUM_ETIKET = {
  yeni: "Yeni",
  işleme_alındı: "İşleme Alındı",
  yönlendirildi: "Yönlendirildi",
  çözüldü: "Çözüldü",
  çözülemedi: "Çözülemedi",
};
const durumEtiket = (d) => DURUM_ETIKET[d] || d || "—";

export default function SistemVatandasDetayPage() {
  const { citizenId } = useParams();
  const navigate = useNavigate();
  const { can, isSuperUser } = usePermissions();
  const canUpdate = can("citizens.update");
  const canPhone = can("citizens.phone.update");
  const canApps = can("citizens.applications.view"); // REVIEW #11: AYRI gate

  const [citizen, setCitizen] = useState(null);
  const [apps, setApps] = useState([]);
  const [appsError, setAppsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setAppsError(false);
    try {
      setCitizen(await fetchCitizen(citizenId));
    } catch {
      setError("Vatandaş yüklenemedi.");
      setLoading(false);
      return;
    }
    // Başvuru geçmişi AYRI gate (citizens.applications.view). 403/hatayı sessizce
    // "boş"a çevirme (REVIEW #11) — yetki-yok / gerçek-boş / hata ayırt edilir.
    if (canApps) {
      try {
        const a = await fetchCitizenApplications(citizenId);
        setApps(Array.isArray(a) ? a : []);
      } catch {
        setAppsError(true);
      }
    }
    setLoading(false);
  }, [citizenId, canApps]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="page-container ye-root"><div className="ye-state">Yükleniyor…</div></div>;
  if (error || !citizen)
    return (
      <div className="page-container ye-root">
        <div className="ye-state ye-state-error">
          {error || "Vatandaş bulunamadı."}
          <button className="btn-md btn-md-ghost" onClick={load}>Tekrar dene</button>
        </div>
      </div>
    );

  return (
    <div className="page-container ye-root">
      <header className="ye-head">
        <div>
          <button className="btn-md btn-md-ghost" onClick={() => navigate("/yonetim/sistem/vatandaslar")}>
            <ArrowLeftIcon width={16} height={16} /> Vatandaşlar
          </button>
          <h1 className="ye-title" style={{ marginTop: 8 }}>
            {citizen.name} {citizen.surname}
          </h1>
          <p className="ye-subtitle">KVKK kapsamındaki kayıt · #{citizen.id}</p>
        </div>
      </header>

      {/* Kimlik / düzenleme */}
      <BilgiKart citizen={citizen} canUpdate={canUpdate} onSaved={setCitizen} />

      {/* Telefon (ayrı, hassas) */}
      <TelefonKart citizen={citizen} canPhone={canPhone} onSaved={setCitizen} />

      {/* Başvuru geçmişi */}
      <section className="md-card ye-section" style={{ padding: "var(--space-md)" }}>
        <h2 className="ye-section-title">
          Başvuru geçmişi{canApps && !appsError ? ` (${apps.length})` : ""}
        </h2>
        {!canApps ? (
          <p className="ye-hint">Başvuru geçmişini görüntüleme yetkiniz yok.</p>
        ) : appsError ? (
          <p className="ye-hint">
            Başvuru geçmişi yüklenemedi.{" "}
            <button className="btn-md btn-md-ghost" onClick={load}>Tekrar dene</button>
          </p>
        ) : apps.length === 0 ? (
          <p className="ye-hint">Bu vatandaşa ait başvuru bulunmuyor.</p>
        ) : (
          <ul className="ye-sub-list">
            {apps.map((a, i) => (
              <li key={`${a.basvuru_no}-${i}`} className="ye-sub-row">
                <span>
                  <strong>{a.basvuru_no}</strong> · {a.konu || "—"}
                  <br />
                  <span className="ye-item-sub">{a.birim || "Birim yok"}</span>
                </span>
                <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <span className="md-chip">{durumEtiket(a.durum)}</span>
                  <br />
                  <span className="ye-item-sub">{fmtTarih(a.tarih)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pasifleştir — yalnız superuser (backend zorlar) */}
      {isSuperUser && <PasiflestirKart citizen={citizen} onDone={() => navigate("/yonetim/sistem/vatandaslar")} />}
    </div>
  );
}

function BilgiKart({ citizen, canUpdate, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: citizen.name, surname: citizen.surname, email: citizen.email || "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const updated = await updateCitizen(citizen.id, { ...form, email: form.email || null });
      onSaved(updated);
      setEdit(false);
    } catch (ex) {
      setErr(backendError(ex, "Güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="md-card ye-section" style={{ padding: "var(--space-md)" }}>
      <div className="ye-modal-actions ye-modal-actions-split" style={{ marginTop: 0 }}>
        <h2 className="ye-section-title">Kimlik bilgileri</h2>
        {canUpdate && !edit && (
          <button className="btn-md btn-md-ghost" onClick={() => setEdit(true)}>Düzenle</button>
        )}
      </div>

      {edit ? (
        <form className="ye-form" onSubmit={submit} autoComplete="off">
          {err && <div className="ye-alert">{err}</div>}
          <div className="ye-field-row">
            <label className="ye-field"><span>Ad</span>
              <input className="md-input" value={form.name} onChange={set("name")} required autoFocus /></label>
            <label className="ye-field"><span>Soyad</span>
              <input className="md-input" value={form.surname} onChange={set("surname")} required /></label>
          </div>
          <label className="ye-field"><span>E-posta</span>
            <input className="md-input" type="email" value={form.email} onChange={set("email")} /></label>
          <p className="ye-hint">Telefon buradan değişmez — ayrı bölümden güncellenir.</p>
          <div className="ye-modal-actions">
            <button type="button" className="btn-md btn-md-ghost" onClick={() => setEdit(false)} disabled={saving}>Vazgeç</button>
            <button type="submit" className="btn-md btn-md-primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Kaydet"}</button>
          </div>
        </form>
      ) : (
        <dl className="ye-kv">
          <dt>Ad</dt><dd>{citizen.name}</dd>
          <dt>Soyad</dt><dd>{citizen.surname}</dd>
          <dt>E-posta</dt><dd>{citizen.email || "—"}</dd>
        </dl>
      )}
    </section>
  );
}

function TelefonKart({ citizen, canPhone, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [phone, setPhone] = useState(citizen.phone_number || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    setOk(false);
    try {
      const res = await updateCitizenPhone(citizen.id, phone);
      onSaved({ ...citizen, phone_number: res?.phone_number ?? phone });
      setOk(true);
      setEdit(false);
    } catch (ex) {
      setErr(backendError(ex, "Telefon güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="md-card ye-section" style={{ padding: "var(--space-md)" }}>
      <div className="ye-modal-actions ye-modal-actions-split" style={{ marginTop: 0 }}>
        <h2 className="ye-section-title">Telefon (hassas)</h2>
        {canPhone && !edit && (
          <button className="btn-md btn-md-ghost" onClick={() => { setEdit(true); setOk(false); }}>Değiştir</button>
        )}
      </div>
      {edit ? (
        <form className="ye-form" onSubmit={submit} autoComplete="off">
          {err && <div className="ye-alert">{err}</div>}
          <label className="ye-field"><span>Yeni telefon (10 hane)</span>
            <input className="md-input" value={phone} onChange={(e) => setPhone(e.target.value)}
              inputMode="numeric" placeholder="5xxxxxxxxx" required autoFocus /></label>
          <p className="ye-hint">Bu değişiklik KVKK gereği ayrı kayıt altına alınır.</p>
          <div className="ye-modal-actions">
            <button type="button" className="btn-md btn-md-ghost" onClick={() => setEdit(false)} disabled={saving}>Vazgeç</button>
            <button type="submit" className="btn-md btn-md-primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Güncelle"}</button>
          </div>
        </form>
      ) : (
        <dl className="ye-kv">
          <dt>Telefon</dt><dd>{citizen.phone_number}{ok ? <span className="md-chip" style={{ marginLeft: 8 }}>Güncellendi</span> : null}</dd>
        </dl>
      )}
    </section>
  );
}

function PasiflestirKart({ citizen, onDone }) {
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const doDelete = async () => {
    setSaving(true);
    setErr("");
    try {
      await deleteCitizen(citizen.id);
      onDone();
    } catch (ex) {
      setErr(backendError(ex, "Pasifleştirilemedi."));
      setSaving(false);
    }
  };

  return (
    <section className="md-card ye-section" style={{ padding: "var(--space-md)" }}>
      <h2 className="ye-section-title">Kaydı pasifleştir</h2>
      <p className="ye-hint">
        Soft-delete: kayıt listeden kaldırılır (fiziksel silinmez), başvurular korunur.
        Yalnız süper kullanıcı yapabilir.
      </p>
      {err && <div className="ye-alert">{err}</div>}
      {confirm ? (
        <div className="ye-modal-actions">
          <button className="btn-md btn-md-ghost" onClick={() => setConfirm(false)} disabled={saving}>Vazgeç</button>
          <button className="btn-md btn-md-danger" onClick={doDelete} disabled={saving}>
            {saving ? "Pasifleştiriliyor…" : "Evet, pasifleştir"}
          </button>
        </div>
      ) : (
        <div className="ye-modal-actions">
          <button className="btn-md btn-md-danger" onClick={() => setConfirm(true)}>Pasifleştir</button>
        </div>
      )}
    </section>
  );
}
